// Analogue clock pictures (21 Oct 2026, Year 3). A `clock` is a round face
// with the numbers 1 to 12, a mark for every minute and two hands. The hour
// hand sits where it really would (part way to the next number), so the
// child reads the time off the hands. The time is never written or spoken:
// reading it is the question.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type ClockSpec = {
  type: 'clock';
  hour: number;
  minute: number;
};

function isWholeIn(value: unknown, low: number, high: number): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= low &&
    value <= high
  );
}

export function readClock(record: Record<string, unknown>): ClockSpec | null {
  const { hour, minute } = record;
  if (!isWholeIn(hour, 1, 12) || !isWholeIn(minute, 0, 59)) return null;
  return { type: 'clock', hour: hour as number, minute: minute as number };
}

// What a screen reader is told in place of the clock: never the time.
export function describeClock(): string {
  return 'A clock face with numbers 1 to 12 and an hour hand and a minute hand.';
}

// Turn of each hand in degrees, clockwise from 12.
export function handAngles(clock: ClockSpec): { hour: number; minute: number } {
  return {
    hour: ((clock.hour % 12) + clock.minute / 60) * 30,
    minute: clock.minute * 6,
  };
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export type ClockMark = { x1: number; y1: number; x2: number; y2: number };

export type ClockFacePicture = {
  centre: number;
  radius: number;
  // 60 marks, every fifth one longer.
  marks: (ClockMark & { long: boolean })[];
  numbers: { value: number; x: number; y: number }[];
  hands: {
    name: 'hour' | 'minute';
    angle: number;
    x: number;
    y: number;
    width: number;
  }[];
  size: number;
};

export const CLOCK_NUMBER_SIZE = 17;
const RADIUS = 100;
const EDGE = 6;

function point(angle: number, distance: number, centre: number) {
  const turn = (angle * Math.PI) / 180;
  return {
    x: Number((centre + Math.sin(turn) * distance).toFixed(2)),
    y: Number((centre - Math.cos(turn) * distance).toFixed(2)),
  };
}

export function clockFacePicture(clock: ClockSpec): ClockFacePicture {
  const centre = RADIUS + EDGE;
  const marks: ClockFacePicture['marks'] = [];
  for (let minute = 0; minute < 60; minute += 1) {
    const long = minute % 5 === 0;
    const from = point(minute * 6, RADIUS - (long ? 12 : 6), centre);
    const to = point(minute * 6, RADIUS - 2, centre);
    marks.push({ x1: from.x, y1: from.y, x2: to.x, y2: to.y, long });
  }
  const numbers = Array.from({ length: 12 }, (_, index) => {
    const value = index + 1;
    return { value, ...point(value * 30, RADIUS - 28, centre) };
  });
  const angles = handAngles(clock);
  const hourEnd = point(angles.hour, RADIUS * 0.5, centre);
  // The minute hand reaches into the minute marks, so the exact minute can be
  // counted off the mark it points at (22 Oct 2026; it used to stop at the
  // numbers).
  const minuteEnd = point(angles.minute, RADIUS * 0.93, centre);
  return {
    centre,
    radius: RADIUS,
    marks,
    numbers,
    hands: [
      { name: 'hour', angle: angles.hour, ...hourEnd, width: 7 },
      { name: 'minute', angle: angles.minute, ...minuteEnd, width: 3 },
    ],
    size: centre * 2,
  };
}
